// The Course Number cell: the coursenum in the monospace style, truncating with its full value disclosed,
// and a copy control that never shrinks. The result shows on the control for a moment and is announced in
// the page's live region (CopyLiveRegion).

import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { Button } from '@CommonComponents/buttons'
import {
  ClippedValue,
  Tooltip,
} from '../../components/me_table/tooltip/Tooltip'
import {
  announceCopy,
  copiedMessage,
  copyFailedMessage,
} from './CopyLiveRegion'

export const COPY_FEEDBACK_MS = 2600
export const COPY_LABEL = 'Copy course number'

export const COPY_ICONS = {
  idle: 'fa-regular fa-copy',
  success: 'fa-regular fa-check',
  failure: 'fa-regular fa-triangle-exclamation',
}

// a missing clipboard API (an insecure context) or a refused write is a failure
const writeClipboard = text => {
  const clipboard = typeof navigator !== 'undefined' && navigator.clipboard
  if (!clipboard || typeof clipboard.writeText !== 'function')
    return Promise.reject(new Error('The clipboard is unavailable.'))
  try {
    return Promise.resolve(clipboard.writeText(text))
  } catch (error) {
    return Promise.reject(error)
  }
}

export const CourseNumberCell = ({ row }) => {
  const coursenum = row.coursenum || ''
  const [result, setResult] = useState('idle')
  const timer = useRef(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearTimeout(timer.current)
    }
  }, [])

  const show = outcome => {
    if (!mounted.current) return
    clearTimeout(timer.current)
    setResult(outcome)
    timer.current = setTimeout(() => setResult('idle'), COPY_FEEDBACK_MS)
    announceCopy(
      outcome === 'success'
        ? copiedMessage(coursenum)
        : copyFailedMessage(coursenum)
    )
  }

  const copy = () =>
    writeClipboard(coursenum).then(
      () => show('success'),
      () => show('failure')
    )

  return (
    <span className="admin-courses-number">
      <ClippedValue text={coursenum} className="me-table-id" />
      <Tooltip text={COPY_LABEL}>
        <Button
          className="me-btn-icon me-btn-icon-sm admin-courses-copy"
          icon={COPY_ICONS[result]}
          aria-label={COPY_LABEL}
          onClick={copy}
        />
      </Tooltip>
    </span>
  )
}

CourseNumberCell.propTypes = {
  row: PropTypes.shape({ coursenum: PropTypes.string }).isRequired,
}
